import { defineStore, storeToRefs } from "pinia";
import { ref } from "vue";
import { watchDebounced } from "@vueuse/core";
import useMyFetch from "@stores/useMyFetch";
import handleError from "@composables/handleError.js";
import { getClientId } from "@utils/clientId";
import { useSurveyStore } from "@stores/survey/survey";
import { captureEvent } from "@/lib/analytics";

export const useMailingListsStore = defineStore("mailingLists", () => {
  // state
  const mailingLists = ref([]);
  const lightMailingLists = ref([]);
  const mailingListsCount = ref(0);
  const mailingListsSearchQuery = ref("");
  const currentPage = ref(1);

  // stores
  const { token } = storeToRefs(useSurveyStore());

  // actions
  const getMailingLists = async (page = 1) => {
    try {
      const { response, data } = await useMyFetch(`/surveys/${token.value}/mailing_lists?page=${page}`).json();

      if (response.value.ok) {
        const { mailing_lists: listsData, mailing_lists_count: listsCountData } = data.value;
        mailingLists.value = listsData;
        mailingListsCount.value = listsCountData;
      }
    } catch (err) {
      handleError(err);
    }
  };

  const getLightMailingLists = async () => {
    try {
      const { response, data } = await useMyFetch(`/surveys/${token.value}/mailing_lists/light`).json();

      if (response.value.ok) lightMailingLists.value = data.value?.mailing_lists;
    } catch (err) {
      handleError(err);
    }
  };

  const searchMailingLists = async (page = 1) => {
    const params = { name: mailingListsSearchQuery.value, page: page };
    try {
      const { response, data } = await useMyFetch(`/surveys/${token.value}/mailing_lists/search`)
        .json()
        .post(params, null);

      if (response.value.ok) {
        const { mailing_lists: listsData, mailing_lists_count: listsCountData } = data.value;
        mailingLists.value = listsData;
        mailingListsCount.value = listsCountData;
        currentPage.value = page;
      }
    } catch (err) {
      handleError(err);
    }
  };

  const removeMailingList = async (listToken) => {
    try {
      const { response } = await useMyFetch(`/surveys/${token.value}/mailing_lists/${listToken}`).delete().json();

      if (response.value.ok) {
        const listIndex = mailingLists.value.findIndex((s) => s.token === listToken);
        if (listIndex !== -1) mailingLists.value.splice(listIndex, 1);

        captureEvent("mailing_list_deleted");
      }
    } catch (err) {
      handleError(err);
    }
  };

  const exportMailingList = async (listToken) => {
    const clientId = getClientId();

    try {
      const { response } = await useMyFetch(`/surveys/${token.value}/mailing_lists/${listToken}/export`)
        .post({ client_id: clientId }, null)
        .json();

      if (response.value.ok) captureEvent("mailing_list_exported");
    } catch (err) {
      handleError(err);
    }
  };

  const changePage = async (newPage) => {
    currentPage.value = newPage;
    await searchMailingLists(newPage);
  };

  // mailing lists channel handlers
  const brevoChannelHandlers = (cable, surveyToken) => {
    return {
      connected: subscribeToChannel(cable, surveyToken),
      rejected: () => console.log("rejected"),
      received: handleReceivedMessage,
    };
  };

  const subscribeToChannel = (cable, surveyToken) => () =>
    cable.perform({ channel: "BrevoChannel", action: "subscribe", data: { survey_token: surveyToken } });

  const handleReceivedMessage = (msg) => {
    if (!msg) return;

    switch (msg.action) {
      case "import_mailing_list": {
        const listToUpdate = mailingLists.value.find((list) => list.token === msg.mailing_list_token);
        if (listToUpdate && msg.contacts_count) listToUpdate.contacts_count = msg.contacts_count;
        break;
      }
      case "export_mailing_list": {
        if (msg.csv_url && msg.mailing_list_name) {
          const link = document.createElement("a");
          link.href = msg.csv_url;
          link.click();
        }
        break;
      }
    }
  };

  watchDebounced(mailingListsSearchQuery, () => searchMailingLists(), { debounce: 300 });

  return {
    // state
    mailingLists,
    lightMailingLists,
    mailingListsCount,
    mailingListsSearchQuery,
    currentPage,
    // actions
    getMailingLists,
    getLightMailingLists,
    searchMailingLists,
    changePage,
    removeMailingList,
    exportMailingList,
    brevoChannelHandlers,
  };
});
